import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Card } from 'react-bootstrap';
import { useUploadCSVMutation } from '../../slices/devicesApiSlice';
import { useGetSitesQuery } from '../../slices/sitesApiSlice';
import { toast } from 'react-toastify';
import Loader from '../Loader';

const PhoneUpload = ({ siteId, visible = false }) => {
  const [isVisible, setIsVisible] = useState(visible);
  const [file, setFile] = useState(null);
  
  // Update isVisible when visible prop changes
  useEffect(() => {
    setIsVisible(visible);
  }, [visible]);
  const [uploadCSV, { isLoading, error }] = useUploadCSVMutation();
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const { data: sites, isLoading: sitesLoading } = useGetSitesQuery();
  const [hscSiteId, setHscSiteId] = useState(null);
  
  // Find the site with abbr=HSC
  useEffect(() => {
    if (sites) {
      const hscSite = sites.find(site => site.abbr === 'HSC');
      if (hscSite) {
        setHscSiteId(hscSite._id);
      }
    }
  }, [sites]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setUploadSuccess(false);
    setUploadResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error('Please select a CSV file to upload');
      return;
    }

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      toast.error('Please upload a valid CSV file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('siteId', hscSiteId || siteId);
      formData.append('useColumnAForLocation', 'true');

      const result = await uploadCSV(formData).unwrap();
      setUploadSuccess(true);
      setUploadResult(result);
      toast.success('CSV file uploaded and processed successfully');
      setFile(null);
      // Reset the file input
      document.getElementById('csvFile').value = '';
    } catch (err) {
      toast.error(err?.data?.message || err.error || 'Failed to upload CSV file');
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Card className="my-3 p-3">
      <Card.Header as="h5">Upload Phone CSV</Card.Header>
      <Card.Body>
        <Card.Text>
          Upload a CSV file to update phone records. This will delete all existing phone records with vendor "xtel" and create new ones from the CSV data.
        </Card.Text>
        <Card.Text>
          <strong>CSV Format:</strong> The file should have at least 3 columns. Column A will be used for the "location" field, Column B for the "user" field, and Column C for the "number" field.
        </Card.Text>
        
        {error && <Alert variant="danger">{error?.data?.message || error.error}</Alert>}
        
        {uploadSuccess && uploadResult && (
          <Alert variant="success">
            CSV processed successfully! Created {uploadResult.devicesCreated} new phone records.
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="csvFile" className="mb-3">
            <Form.Label>Select CSV File</Form.Label>
            <Form.Control 
              type="file" 
              onChange={handleFileChange}
              accept=".csv"
            />
            <Form.Text className="text-muted">
              Only CSV files are accepted
            </Form.Text>
          </Form.Group>
          
          <Button 
            variant="primary" 
            type="submit" 
            disabled={!file || isLoading}
          >
            {isLoading ? <Loader /> : 'Upload and Process'}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
};

// Export component with visibility control
export default PhoneUpload;
export const togglePhoneUpload = (setVisible) => {
  setVisible(prevState => !prevState);
};
